// Stripe product and price IDs (test mode)
// To switch to production: replace with live mode IDs and update env vars

export const STRIPE_CONFIG = {
    // Products
    products: {
        starter: 'prod_Tww8Dnv5RaS1Rj',
        pro: 'prod_Tww8VKhnGquKH3',
        creditPack100: 'prod_TwlH5E345PvgmP',
        creditPack500: 'prod_TwlHbFu2U5oKzz',
        creditPack1500: 'prod_TwlHBkj7hxOuku',
    },

    // Prices
    prices: {
        // Subscription prices (recurring)
        starterMonthly: 'price_1Sz2GRJtyB9kWazghXzuggab',  // $9.99/month
        proMonthly: 'price_1Sz2GiJtyB9kWazgSuBzVQ8P',      // $24.99/month

        // Credit pack prices (one-time)
        creditPack100: 'price_1SyrknJtyB9kWazglo4eRShs',   // $2.49
        creditPack500: 'price_1SyrkoJtyB9kWazgSIvuQ6yG',   // $9.99
        creditPack1500: 'price_1SyrkpJtyB9kWazg2i6x0gjp',  // $29.99
    },

    // Credits granted per product
    creditsPerProduct: {
        starter: 500,
        pro: 1500,
        creditPack100: 100,
        creditPack500: 500,
        creditPack1500: 1500,
    },

    // Tier mapping from price ID to subscription tier
    tierFromPriceId: (priceId: string): 'starter' | 'pro' | null => {
        if (priceId === STRIPE_CONFIG.prices.starterMonthly) return 'starter';
        if (priceId === STRIPE_CONFIG.prices.proMonthly) return 'pro';
        return null;
    },

    // Credits from price ID (for one-time credit packs)
    creditsFromPriceId: (priceId: string): number => {
        if (priceId === STRIPE_CONFIG.prices.creditPack100) return 100;
        if (priceId === STRIPE_CONFIG.prices.creditPack500) return 500;
        if (priceId === STRIPE_CONFIG.prices.creditPack1500) return 1500;
        return 0;
    },
} as const;

// Display info for pricing page
export type PricingFeature = { text: string; included: boolean };

export const PRICING_TIERS = [
    {
        id: 'free' as const,
        name: 'Free Plan',
        description: 'Our starter plan.',
        price: 0,
        priceLabel: '$0',
        priceMonthly: 0,
        credits: 50,
        creditsLabel: '50 one-time signup bonus',
        badge: null as string | null,
        features: [
            { text: 'Access to all generation models', included: true },
            { text: '50 credits (one-time signup bonus)', included: true },
            { text: 'Public gallery access', included: true },
            { text: 'Standard generation queue', included: true },
            { text: 'No commercial use', included: false },
            { text: 'No private mode', included: false },
            { text: 'No add-on credit purchases', included: false },
        ] as PricingFeature[],
        priceId: null as string | null,
        highlighted: false,
    },
    {
        id: 'starter' as const,
        name: 'Starter Plan',
        description: 'More credits and private mode.',
        price: 999,
        priceLabel: '$9.99/mo',
        priceMonthly: 9.99,
        credits: 500,
        creditsLabel: '500 credits/month',
        badge: null as string | null,
        features: [
            { text: 'Access to all generation models', included: true },
            { text: '500 credits, refreshes monthly', included: true },
            { text: 'Private mode for generations', included: true },
            { text: 'Standard generation queue', included: true },
            { text: 'Ability to purchase add-on credits', included: true },
            { text: 'No priority generation', included: false },
        ] as PricingFeature[],
        priceId: STRIPE_CONFIG.prices.starterMonthly,
        highlighted: false,
    },
    {
        id: 'pro' as const,
        name: 'Pro Plan',
        description: 'Maximum credits and every feature unlocked.',
        price: 2499,
        priceLabel: '$24.99/mo',
        priceMonthly: 24.99,
        credits: 1500,
        creditsLabel: '1,500 credits/month',
        badge: 'MOST POPULAR',
        features: [
            { text: 'Access to all generation models', included: true },
            { text: '1,500 credits, refreshes monthly', included: true },
            { text: 'Private mode for generations', included: true },
            { text: 'Priority generation queue', included: true },
            { text: 'Commercial use rights', included: true },
            { text: 'Ability to purchase add-on credits', included: true },
            { text: 'Early access to new features', included: true },
        ] as PricingFeature[],
        priceId: STRIPE_CONFIG.prices.proMonthly,
        highlighted: true,
    },
] as const;

export const CREDIT_PACKS = [
    {
        credits: 100,
        price: 249,
        priceLabel: '$2.49',
        perCredit: '$0.025',
        priceId: STRIPE_CONFIG.prices.creditPack100,
    },
    {
        credits: 500,
        price: 999,
        priceLabel: '$9.99',
        perCredit: '$0.020',
        priceId: STRIPE_CONFIG.prices.creditPack500,
        popular: true,
    },
    {
        credits: 1500,
        price: 2999,
        priceLabel: '$29.99',
        perCredit: '$0.020',
        priceId: STRIPE_CONFIG.prices.creditPack1500,
    },
] as const;
